import React, { useState, useEffect, useMemo, useRef } from 'react';
import { ShoppingCart, X, Plus, Trash2, Printer, CheckCircle, AlertTriangle, Link2, DollarSign, Search, ArrowUpDown, Loader2, Sparkles, BookOpen, ShieldCheck, RotateCcw, Save, Lock, CloudUpload, Tag, Eye, MessageCircle, ExternalLink } from 'lucide-react';
import { InventoryItem } from '../types/inventory';
import { OABLineItem, OABHeader, OrderReference, computePackagingSuggestion, computeOABUrgencySummary, computeOABStoresSummary, PriorityType, normalizePriority } from '../types/oab';
import { OABStageNotifyPanel } from './OABStageNotifyPanel';
import { generateFolioOAB, fetchNextFolioOAB, createOABSheet, saveOABDraft } from '../services/oabService';
import { bulkUpdateCosts } from '../services/catalogService';
import { getCompatibleUoMs, convertUom, normalizeUomCode } from '../services/uomService';
import { OrderSearchModal } from './OrderSearchModal';
import { PrintSheetOAB } from './PrintSheetOAB';
import { PrintSheetProjectLabels } from './PrintSheetProjectLabels';
import { QuickAddCatalogItemModal } from './QuickAddCatalogItemModal';
import { OABDraftRestoreModal, OABDraftPayload } from './OABDraftRestoreModal';
import { OABExitConfirmModal } from './OABExitConfirmModal';

interface SupplyOrderModalProps {
  isOpen: boolean;
  onClose: () => void;
  inventoryItems: InventoryItem[];
  bcvRate: number;
  initialDraftItems?: InventoryItem[];
  initialDraftLines?: OABLineItem[]; // PLAN-034: Soporte de re-solicitud de ítems rechazados
  initialNotes?: string;              // PLAN-034: Justificación contextual de origen
  onOrderCreated?: (createdLines: OABLineItem[]) => void;
  onOpenReview?: (folio?: string) => void;
  onItemCreated?: (item: InventoryItem) => void;
  currentUser?: {
    name?: string;
    permissions?: string[];
    puestos?: string[];
  } | null;
}

type SortCriterion = 'nombre' | 'costo' | 'subtotal';
type SortDirection = 'asc' | 'desc';

const PRIORITY_ORDER: PriorityType[] = ['Urgente', 'Alta', 'Media', 'Baja', 'Por Pedido'];

const PRIORITY_THEME: Record<PriorityType, {
  label: string;
  icon: string;
  badgeClass: string;
  borderClass: string;
  headerBg: string;
}> = {
  Urgente: {
    label: 'URGENTE',
    icon: '🔴',
    badgeClass: 'bg-rose-500/20 text-rose-300 border-rose-500/40',
    borderClass: 'border-rose-500/30',
    headerBg: 'bg-rose-950/30',
  },
  Alta: {
    label: 'ALTA',
    icon: '🟠',
    badgeClass: 'bg-amber-500/20 text-amber-300 border-amber-500/40',
    borderClass: 'border-amber-500/30',
    headerBg: 'bg-amber-950/30',
  },
  Media: {
    label: 'MEDIA',
    icon: '🟡',
    badgeClass: 'bg-yellow-500/20 text-yellow-300 border-yellow-500/40',
    borderClass: 'border-yellow-500/30',
    headerBg: 'bg-yellow-950/20',
  },
  Baja: {
    label: 'BAJA',
    icon: '🟢',
    badgeClass: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/40',
    borderClass: 'border-emerald-500/30',
    headerBg: 'bg-emerald-950/20',
  },
  'Por Pedido': {
    label: 'POR PEDIDO',
    icon: '🔵',
    badgeClass: 'bg-sky-500/20 text-sky-300 border-sky-500/40',
    borderClass: 'border-sky-500/30',
    headerBg: 'bg-sky-950/30',
  },
};

const DRAFT_STORAGE_KEY = 'sanesca_oab_draft_v1';

export type { OABDraftPayload };

interface WhatsAppAlertPayload {
  folio: string;
  totalUSD: number;
  totalBs: number;
  tasaBCV: number;
  linesCount: number;
  urgentLines?: number;
  phone?: string;
}

export function generateGerenciaWhatsAppLink({
  folio,
  totalUSD,
  totalBs,
  tasaBCV,
  linesCount,
  urgentLines = 0,
  phone = '584143285741'
}: WhatsAppAlertPayload): string {
  const baseUrl = `https://wa.me/${phone}`;
  const urgentText = urgentLines > 0 ? ` (🔴 ${urgentLines} urgentes)` : '';
  const message =
    `🔴 *ALERTA SANESCA PRO — OAB EMITIDA*\n\n` +
    `*Folio:* ${folio}\n` +
    `*Fecha:* ${new Date().toISOString().split('T')[0]}\n` +
    `*Total Estimado:* $${totalUSD.toFixed(2)} USD (Bs. ${totalBs.toLocaleString('es-VE', { minimumFractionDigits: 2 })})\n` +
    `*Tasa BCV:* Bs. ${tasaBCV.toFixed(2)}\n` +
    `*Renglones:* ${linesCount} material(es)${urgentText}\n` +
    `*Estado:* Solicitado para revisión de Compras / Gerencia\n\n` +
    `_Revisar y autorizar en el sistema:_\n` +
    `https://sanesca-inventario.pages.dev/?folio=${encodeURIComponent(folio)}`;

  return `${baseUrl}?text=${encodeURIComponent(message)}`;
}

export const SupplyOrderModal: React.FC<SupplyOrderModalProps> = ({
  isOpen,
  onClose,
  inventoryItems,
  bcvRate,
  initialDraftItems = [],
  initialDraftLines,
  initialNotes,
  onOrderCreated,
  onOpenReview,
  onItemCreated,
  currentUser = null
}) => {
  const [folio, setFolio] = useState('');
  const [fechaEmision, setFechaEmision] = useState('');
  const [tasa, setTasa] = useState(Number((bcvRate || 880.00).toFixed(2)));

  useEffect(() => {
    if (bcvRate && bcvRate > 0) {
      setTasa(Number(bcvRate.toFixed(2)));
    }
  }, [bcvRate]);
  const [notas, setNotas] = useState('');
  const [lines, setLines] = useState<OABLineItem[]>([]);
  const [orderModalOpen, setOrderModalOpen] = useState(false);
  const [activeLineIdForOrder, setActiveLineIdForOrder] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submittedHeader, setSubmittedHeader] = useState<OABHeader | null>(null);
  const [showPrintSheet, setShowPrintSheet] = useState(false);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);

  // Máquina de Estados Post-Emisión y Borradores Centralizados (PLAN-033 - Odoo 18)
  type ModalStage = 'EDIT_DRAFT' | 'SUBMITTING' | 'EMITTED_SUCCESS';
  const [modalStage, setModalStage] = useState<ModalStage>('EDIT_DRAFT');
  const [currentOabId, setCurrentOabId] = useState<string | null>(null);
  const [isSavingDraftCloud, setIsSavingDraftCloud] = useState(false);
  const [showLabelsSheet, setShowLabelsSheet] = useState(false);
  const [hasPrintedRouteSheet, setHasPrintedRouteSheet] = useState(false);

  // Estados Anti-Reset y Borrador Persistente (PLAN-015 - Ruta A)
  const [pendingDraft, setPendingDraft] = useState<OABDraftPayload | null>(null);
  const [showDraftWelcomeModal, setShowDraftWelcomeModal] = useState(false);
  const [showExitConfirmModal, setShowExitConfirmModal] = useState(false);
  const isInitializedRef = useRef(false);

  // Criterios de ordenamiento dentro de cada grupo
  const [sortCriterion, setSortCriterion] = useState<SortCriterion>('subtotal');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  // Estados para el Buscador Reactivo (Flujo Rápido de Taller)
  const [searchTerm, setSearchTerm] = useState('');
  const [isSearchDropdownOpen, setIsSearchDropdownOpen] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Submodal: Alta Rápida de Insumo al Vuelo (PLAN-044)
  const [isQuickAddOpen, setIsQuickAddOpen] = useState(false);

  const searchResults = useMemo(() => {
    if (!searchTerm.trim()) return [];
    const term = searchTerm.toLowerCase().trim();
    return inventoryItems
      .filter(item => !lines.some(l => l.dashboardId === item.id))
      .filter(item =>
        item.nombre.toLowerCase().includes(term) ||
        (item.codigo && item.codigo.toLowerCase().includes(term)) ||
        (item.marca && item.marca.toLowerCase().includes(term))
      )
      .slice(0, 8);
  }, [searchTerm, inventoryItems, lines]);

  // Función de Inicialización Limpia de Requisición
  const initializeFreshDraft = (customItems?: InventoryItem[]) => {
    setModalStage('EDIT_DRAFT');
    setCurrentOabId(null);
    setFolio(generateFolioOAB());
    fetchNextFolioOAB(true).then(nextFolio => {
      if (nextFolio) setFolio(nextFolio);
    }).catch(err => {
      console.warn('Error obteniendo folio atómico previo:', err);
    });
    setFechaEmision(new Date().toISOString().split('T')[0]);
    setTasa(Number((bcvRate || 880.00).toFixed(2)));
    setSubmittedHeader(null);
    setStatusMessage(null);
    setNotas('');
    setHasPrintedRouteSheet(false);

    const sourceItems = (customItems && customItems.length > 0)
      ? customItems
      : (initialDraftItems.length > 0
          ? initialDraftItems
          : inventoryItems.filter(i => (i.deficit || 0) > 0));

    const generatedLines: OABLineItem[] = sourceItems.map(item => {
      const rawDef = Math.max(0, (item.stockMinimo || 0) - (item.stockBase || 0));
      const def = (item.deficit && item.deficit > 0) ? item.deficit : (rawDef > 0 ? rawDef : 1);
      const pack = computePackagingSuggestion(item.nombre, item.categoriaMaterial, def);
      const cost = item.costoUnitarioUSD || 1.0;
      const baseUom = normalizeUomCode(item.unidad || item.unidadMedida || 'UND');
      return {
        nombre: item.nombre,
        insumoId: item.insumoId || item.id,
        dashboardId: item.id,
        cantidadStock: item.stockBase || 0,
        stockMinimo: item.stockMinimo || 0,
        deficit: item.deficit || 0,
        cantidadSugerida: def,
        cantidadSolicitada: pack.cantidadComercialSugerida,
        empaqueComercial: pack.empaqueComercial,
        factorEmpaque: pack.factorEmpaque,
        paquetesSugeridos: pack.paquetesSugeridos,
        cantidadComercialSugerida: pack.cantidadComercialSugerida,
        costoUnitarioUSD: cost,
        subtotalUSD: pack.cantidadComercialSugerida * cost,
        prioridad: normalizePriority(item.prioridad),
        unidadMedida: baseUom,
        uomCompra: baseUom,
        factorUoM: 1.0,
        cantidadCompra: pack.cantidadComercialSugerida,
        costoCompraUSD: cost,
        cantEnBulto: item.cant_en_bulto
      };
    });

    setLines(generatedLines);
    isInitializedRef.current = true;
  };

  // Manejador de Apertura y Detección de Borrador en sessionStorage
  useEffect(() => {
    if (!isOpen) {
      isInitializedRef.current = false;
      setPendingDraft(null);
      setShowDraftWelcomeModal(false);
      setShowExitConfirmModal(false);
      return;
    }

    if (isInitializedRef.current) return;

    // PLAN-034: Si se inyectaron líneas de re-solicitud explícitamente desde revisión
    if (initialDraftLines && initialDraftLines.length > 0) {
      setModalStage('EDIT_DRAFT');
      setCurrentOabId(null);
      setFolio(generateFolioOAB());
      fetchNextFolioOAB(true).then(nextFolio => {
        if (nextFolio) setFolio(nextFolio);
      }).catch(err => {
        console.warn('Error obteniendo folio atómico previo:', err);
      });
      setFechaEmision(new Date().toISOString().split('T')[0]);
      setTasa(Number((bcvRate || 880.00).toFixed(2)));
      setSubmittedHeader(null);
      setStatusMessage(null);
      setNotas(initialNotes || '');
      setLines(initialDraftLines.map(l => ({ ...l, prioridad: normalizePriority(l.prioridad) })));
      isInitializedRef.current = true;
      try {
        sessionStorage.removeItem(DRAFT_STORAGE_KEY);
      } catch (e) {}
      return;
    }

    try {
      const saved = sessionStorage.getItem(DRAFT_STORAGE_KEY);
      if (saved) {
        const parsed: OABDraftPayload = JSON.parse(saved);
        if (parsed && Array.isArray(parsed.lines) && parsed.lines.length > 0) {
          setPendingDraft(parsed);
          setShowDraftWelcomeModal(true);
          return;
        }
      }
    } catch (e) {
      console.warn('Error leyendo borrador OAB de sessionStorage:', e);
    }

    initializeFreshDraft();
  }, [isOpen, initialDraftLines, initialNotes]);

  // Restaurar borrador seleccionado por el usuario
  const handleRestoreDraft = () => {
    if (!pendingDraft) return;
    setFolio(pendingDraft.folio || generateFolioOAB());
    setFechaEmision(pendingDraft.fechaEmision || new Date().toISOString().split('T')[0]);
    setTasa(pendingDraft.tasaBCV || Number((bcvRate || 880.00).toFixed(2)));
    setNotas(pendingDraft.notas || '');
    setLines((pendingDraft.lines || []).map(l => ({ ...l, prioridad: normalizePriority(l.prioridad) })));
    setModalStage('EDIT_DRAFT');
    setShowDraftWelcomeModal(false);
    setPendingDraft(null);
    isInitializedRef.current = true;
  };

  // Descartar borrador e iniciar nueva orden limpia
  const handleDiscardDraftAndStartFresh = () => {
    try {
      sessionStorage.removeItem(DRAFT_STORAGE_KEY);
    } catch (e) {}
    setShowDraftWelcomeModal(false);
    setPendingDraft(null);
    initializeFreshDraft();
  };

  // Auto-guardado en sessionStorage ante cada edición del usuario (Bloqueado tras emisión exitosa)
  useEffect(() => {
    if (!isOpen || !isInitializedRef.current) return;
    if (modalStage === 'EMITTED_SUCCESS') return; // PLAN-033: Bloqueo anti-zombie post-emisión
    if (lines.length === 0) return;

    const draftData: OABDraftPayload = {
      folio,
      fechaEmision,
      tasaBCV: tasa,
      notas,
      lines,
      savedAt: new Date().toISOString()
    };

    try {
      sessionStorage.setItem(DRAFT_STORAGE_KEY, JSON.stringify(draftData));
    } catch (e) {
      console.warn('Error guardando borrador en sessionStorage:', e);
    }
  }, [lines, notas, tasa, folio, isOpen, modalStage]);

  // Cierre final limpio sin diálogos de borrador tras emisión exitosa
  const handleFinishAndClose = () => {
    try {
      sessionStorage.removeItem(DRAFT_STORAGE_KEY);
    } catch (e) {}
    setModalStage('EDIT_DRAFT');
    setCurrentOabId(null);
    setSubmittedHeader(null);
    setLines([]);
    onClose();
  };

  // Cierre seguro con diálogo de confirmación si hay modificaciones sin emitir
  const handleAttemptClose = () => {
    if (modalStage === 'EMITTED_SUCCESS') {
      handleFinishAndClose();
      return;
    }
    if (lines.length > 0) {
      setShowExitConfirmModal(true);
    } else {
      onClose();
    }
  };

  // Manejo accesible de tecla Escape (PLAN-022 / PLAN-033)
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (showLabelsSheet) {
          setShowLabelsSheet(false);
          return;
        }
        if (isSearchDropdownOpen) {
          setIsSearchDropdownOpen(false);
          return;
        }
        if (isQuickAddOpen) {
          setIsQuickAddOpen(false);
          return;
        }
        if (showDraftWelcomeModal) {
          setShowDraftWelcomeModal(false);
          return;
        }
        if (showExitConfirmModal) {
          setShowExitConfirmModal(false);
          return;
        }
        if (modalStage === 'EMITTED_SUCCESS') {
          handleFinishAndClose();
          return;
        }
        handleAttemptClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, isSearchDropdownOpen, isQuickAddOpen, showDraftWelcomeModal, showExitConfirmModal, showLabelsSheet, modalStage, lines.length]);

  const handleSaveDraftAndExit = () => {
    if (lines.length > 0) {
      const draftData: OABDraftPayload = {
        folio,
        fechaEmision,
        tasaBCV: tasa,
        notas,
        lines,
        savedAt: new Date().toISOString()
      };
      try {
        sessionStorage.setItem(DRAFT_STORAGE_KEY, JSON.stringify(draftData));
      } catch (e) {}
    }
    setShowExitConfirmModal(false);
    onClose();
  };

  const handleDiscardAndExit = () => {
    try {
      sessionStorage.removeItem(DRAFT_STORAGE_KEY);
    } catch (e) {}
    setShowExitConfirmModal(false);
    onClose();
  };

  // Guardar Borrador formal en Notion ERP (Nube - PLAN-033)
  const handleSaveDraftCloud = async () => {
    if (lines.length === 0) {
      alert('Debe haber al menos un insumo en la orden para guardar borrador.');
      return;
    }

    setIsSavingDraftCloud(true);
    setStatusMessage('Guardando borrador en Notion ERP...');

    try {
      const payload = {
        oabId: currentOabId || undefined,
        folio,
        fechaEmision,
        tasaBCV: tasa,
        totalUSD,
        totalBs,
        notas,
        lineas: lines
      };

      const result = await saveOABDraft(payload);
      if (result.oabId) {
        setCurrentOabId(result.oabId);
      }
      setStatusMessage('¡Borrador guardado exitosamente en Notion ERP!');
    } catch (err: any) {
      console.error('Error guardando borrador en la nube:', err);
      setStatusMessage(`Error: ${err.message}`);
    } finally {
      setIsSavingDraftCloud(false);
    }
  };

  // Agrupación reactiva por Prioridad Operativa con numeración global correlativa
  const groupedLines = useMemo(() => {
    let globalCounter = 1;
    const groups: {
      priority: PriorityType;
      subtotal: number;
      items: { line: OABLineItem; globalIndex: number }[];
    }[] = [];

    const sortFn = (items: OABLineItem[]) => {
      return [...items].sort((a, b) => {
        if (sortCriterion === 'nombre') {
          const cmp = a.nombre.localeCompare(b.nombre, 'es', { sensitivity: 'base' });
          return sortDirection === 'asc' ? cmp : -cmp;
        }
        if (sortCriterion === 'costo') {
          const diff = (a.costoUnitarioUSD || 0) - (b.costoUnitarioUSD || 0);
          return sortDirection === 'asc' ? diff : -diff;
        }
        if (sortCriterion === 'subtotal') {
          const diff = (a.subtotalUSD || 0) - (b.subtotalUSD || 0);
          return sortDirection === 'asc' ? diff : -diff;
        }
        return 0;
      });
    };

    for (const prio of PRIORITY_ORDER) {
      const matching = lines.filter(l => normalizePriority(l.prioridad) === prio);
      if (matching.length > 0) {
        const sorted = sortFn(matching);
        const itemsWithIndex = sorted.map(line => ({
          line,
          globalIndex: globalCounter++
        }));
        const subtotal = matching.reduce((sum, l) => sum + (l.subtotalUSD || 0), 0);
        groups.push({
          priority: prio,
          subtotal,
          items: itemsWithIndex
        });
      }
    }

    return groups;
  }, [lines, sortCriterion, sortDirection]);

  if (!isOpen) return null;

  const getLineKey = (line: OABLineItem): string => line.dashboardId || line.insumoId || line.nombre;

  const handleUomChange = (lineId: string, newUom: string) => {
    setLines(prev => prev.map(line => {
      if (getLineKey(line) !== lineId) return line;
      const baseUom = line.unidadMedida || 'UND';
      const purchaseQty = line.cantidadCompra ?? line.cantidadSolicitada;
      const purchaseCost = line.costoCompraUSD ?? line.costoUnitarioUSD;
      const conv = convertUom(purchaseQty, newUom, baseUom, { cant_en_bulto: line.cantEnBulto }, purchaseCost);

      return {
        ...line,
        uomCompra: newUom,
        factorUoM: conv.factor,
        cantidadSolicitada: conv.targetQty,
        costoUnitarioUSD: conv.unitCostBaseUSD || line.costoUnitarioUSD,
        subtotalUSD: conv.targetQty * (conv.unitCostBaseUSD || line.costoUnitarioUSD),
        formulaUoM: conv.formulaDisplay
      };
    }));
  };

  const handleQuantityChange = (lineId: string, qty: number) => {
    setLines(prev => prev.map(line => {
      if (getLineKey(line) !== lineId) return line;
      const validQty = Math.max(0, qty);
      const baseUom = line.unidadMedida || 'UND';
      const uomCompra = line.uomCompra || baseUom;
      const purchaseCost = line.costoCompraUSD ?? line.costoUnitarioUSD;

      const conv = convertUom(validQty, uomCompra, baseUom, { cant_en_bulto: line.cantEnBulto }, purchaseCost);

      return {
        ...line,
        cantidadCompra: validQty,
        cantidadSolicitada: conv.targetQty,
        factorUoM: conv.factor,
        formulaUoM: conv.formulaDisplay,
        subtotalUSD: conv.targetQty * (line.costoUnitarioUSD || 0)
      };
    }));
  };

  const handleCostChange = (lineId: string, cost: number) => {
    setLines(prev => prev.map(line => {
      if (getLineKey(line) !== lineId) return line;
      const validCost = Math.max(0, cost);
      const factor = line.factorUoM || 1.0;
      const baseCost = factor > 0 ? validCost / factor : validCost;

      return {
        ...line,
        costoCompraUSD: validCost,
        costoUnitarioUSD: baseCost,
        subtotalUSD: (line.cantidadSolicitada || 0) * baseCost
      };
    }));
  };

  const handlePriorityChange = (lineId: string, prio: string) => {
    setLines(prev => prev.map(line => {
      if (getLineKey(line) !== lineId) return line;
      return { ...line, prioridad: prio };
    }));
  };

  const handleRemoveLine = (lineId: string) => {
    setLines(prev => prev.filter(line => getLineKey(line) !== lineId));
  };

  const handleAddManualItem = (item: InventoryItem) => {
    if (lines.some(l => l.dashboardId === item.id || l.nombre === item.nombre)) return;
    const rawDef = Math.max(0, (item.stockMinimo || 0) - (item.stockBase || 0));
    const def = (item.deficit && item.deficit > 0) ? item.deficit : (rawDef > 0 ? rawDef : 1);
    const pack = computePackagingSuggestion(item.nombre, item.categoriaMaterial, def);
    const cost = item.costoUnitarioUSD || 1.0;
    const baseUom = normalizeUomCode(item.unidad || item.unidadMedida || 'UND');
    setLines(prev => [
      ...prev,
      {
        nombre: item.nombre,
        insumoId: item.insumoId || item.id,
        dashboardId: item.id,
        cantidadStock: item.stockBase || 0,
        stockMinimo: item.stockMinimo || 0,
        deficit: item.deficit || 0,
        cantidadSugerida: def,
        cantidadSolicitada: pack.cantidadComercialSugerida,
        empaqueComercial: pack.empaqueComercial,
        factorEmpaque: pack.factorEmpaque,
        paquetesSugeridos: pack.paquetesSugeridos,
        cantidadComercialSugerida: pack.cantidadComercialSugerida,
        costoUnitarioUSD: cost,
        subtotalUSD: pack.cantidadComercialSugerida * cost,
        prioridad: normalizePriority(item.prioridad),
        unidadMedida: baseUom,
        uomCompra: baseUom,
        factorUoM: 1.0,
        cantidadCompra: pack.cantidadComercialSugerida,
        costoCompraUSD: cost,
        cantEnBulto: item.cant_en_bulto
      }
    ]);
  };

  const handleSelectOrderForLine = (order: OrderReference) => {
    if (activeLineIdForOrder !== null) {
      setLines(prev => prev.map(line => {
        if (getLineKey(line) !== activeLineIdForOrder) return line;
        const isStk = !order.id || order.codigo === 'STK' || (order.proyecto && order.proyecto.toLowerCase().includes('stock general'));
        if (isStk) {
          return {
            ...line,
            pedidoId: undefined,
            proyectoId: undefined,
            proyectoNombre: undefined,
            tiendaNombre: undefined,
            clienteNombre: undefined
          };
        }
        return {
          ...line,
          pedidoId: order.id,
          proyectoId: order.proyectoId || order.id,
          tiendaNombre: order.proyecto,
          clienteNombre: order.cliente,
          proyectoNombre: order.cliente
            ? `${order.codigo} • Tienda: ${order.proyecto} • Cliente: ${order.cliente}`
            : `${order.codigo} - ${order.proyecto}`
        };
      }));
      setActiveLineIdForOrder(null);
    }
  };

  const handleOpenQuickAdd = () => {
    const hasPerm = !currentUser || 
                    currentUser?.permissions?.includes('Emitir_OAB') || 
                    currentUser?.permissions?.includes('Superadmin') ||
                    currentUser?.puestos?.some(p => p.toLowerCase().includes('compra') || p.toLowerCase().includes('gerenc'));
    if (!hasPerm) {
      alert('Se requiere permiso de Emisión de OAB, Compras o Superadmin para registrar nuevos insumos al vuelo.');
      return;
    }
    setIsQuickAddOpen(true);
  };

  // Calculations
  const totalUSD = lines.reduce((acc, l) => acc + (l.subtotalUSD || 0), 0);
  const totalBs = totalUSD * tasa;

  const handleSaveToNotion = async () => {
    if (lines.length === 0) {
      alert('Debe haber al menos un insumo en la orden.');
      return;
    }

    setIsSubmitting(true);
    setStatusMessage('Emitiendo Orden de Abastecimiento en Notion ERP...');

    try {
      const payload = {
        oabId: currentOabId || undefined,
        folio,
        fechaEmision,
        tasaBCV: tasa,
        totalUSD,
        totalBs,
        notas,
        lineas: lines
      };

      const result = await createOABSheet(payload);

      const headerObj: OABHeader = {
        id: result.oabId || currentOabId || undefined,
        folio,
        fechaEmision,
        totalUSD,
        totalBs,
        tasaBCV: tasa,
        estadoGeneral: 'Solicitado',
        notas
      };

      setSubmittedHeader(headerObj);
      setModalStage('EMITTED_SUCCESS');
      setShowPrintSheet(true); // Vista previa inmediata de la Hoja Viajera Carta
      setStatusMessage('¡OAB emitida exitosamente en Notion!');

      // Purgar inmediatamente sessionStorage para evitar revivir borrador como zombie
      try {
        sessionStorage.removeItem(DRAFT_STORAGE_KEY);
      } catch (e) {}

      if (onOrderCreated) {
        onOrderCreated(lines);
      }

      // Auto-actualización defensiva Odoo 18 de insumos con costo $0.00 en inventario
      const zeroCostUpdates: Array<{ insumoId: string; costUSD: number }> = [];
      for (const line of lines) {
        const matchingItem = inventoryItems.find(i => i.id === line.dashboardId || i.insumoId === line.insumoId);
        if (matchingItem && (!matchingItem.costoUnitarioUSD || matchingItem.costoUnitarioUSD <= 0)) {
          const validCost = line.costoUnitarioUSD || 0;
          if (validCost > 0) {
            const targetId = line.insumoId || line.dashboardId || matchingItem.id;
            zeroCostUpdates.push({ insumoId: targetId, costUSD: validCost });
          }
        }
      }

      if (zeroCostUpdates.length > 0) {
        bulkUpdateCosts(zeroCostUpdates).then((bRes: any) => {
          console.info(`[Auto-Costo Odoo 18] ${bRes?.updatedCount ?? 0} insumos con costo $0.00 actualizados defensivamente.`);
        }).catch((err: any) => {
          console.warn('[Auto-Costo Odoo 18] Advertencia actualizando costos en lote:', err);
        });
      }
    } catch (err: any) {
      console.error('Error al emitir OAB:', err);
      setStatusMessage(`Error: ${err.message}`);
      setModalStage('EDIT_DRAFT');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-3 sm:p-4 no-print">
        <div className="bg-surface border border-borderSubtle rounded-xl w-full max-w-5xl max-h-[92dvh] sm:max-h-[90vh] flex flex-col overflow-hidden shadow-2xl">
          
          {/* Modal Header */}
          <div className="px-5 py-3 bg-surfaceHigh border-b border-borderSubtle flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <div className={`p-1.5 rounded-lg border ${
                modalStage === 'EMITTED_SUCCESS'
                  ? 'bg-emerald-500/20 text-emerald-400 border-emerald-500/40'
                  : 'bg-brand-500/20 text-brand-400 border-brand-500/40'
              }`}>
                {modalStage === 'EMITTED_SUCCESS' ? (
                  <CheckCircle className="w-5 h-5" />
                ) : (
                  <ShoppingCart className="w-5 h-5" />
                )}
              </div>
              <div>
                <h2 className="text-sm font-bold text-slate-100 tracking-tight flex items-center gap-2">
                  <span>
                    {modalStage === 'EMITTED_SUCCESS'
                      ? 'Entrega Operativa de Abastecimiento'
                      : 'Modo Sugerencias de Abastecimiento'}
                  </span>
                  <span className="font-mono text-xs text-brand-400 bg-page px-2 py-0.5 rounded border border-borderSubtle">
                    {folio}
                  </span>
                  {modalStage === 'EMITTED_SUCCESS' && (
                    <span className="text-[10px] font-mono text-emerald-300 bg-emerald-500/20 px-2 py-0.5 rounded border border-emerald-500/40 uppercase font-semibold">
                      Emitido en Notion
                    </span>
                  )}
                </h2>
                <p className="text-[11px] text-slate-400">
                  {modalStage === 'EMITTED_SUCCESS'
                    ? 'La orden ha sido asentada. Imprime los comprobantes o rótulos y audita el flujo.'
                    : 'Cálculo automático de déficit y consolidación de la Hoja Viajera para Gerencia y Compras'}
                </p>
              </div>
            </div>
            <button
              onClick={handleAttemptClose}
              className="text-slate-400 hover:text-white p-1 rounded-md hover:bg-surfaceHighest transition min-w-[36px] min-h-[36px] flex items-center justify-center"
              title={modalStage === 'EMITTED_SUCCESS' ? 'Cerrar y finalizar' : 'Cerrar'}
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Form Meta Bar */}
          <div className="p-3 bg-page border-b border-borderSubtle grid grid-cols-1 sm:grid-cols-4 gap-3 text-xs">
            <div>
              <label htmlFor="oab_folio" className="flex items-center justify-between text-[10px] uppercase text-slate-500 font-semibold mb-1">
                <span>Folio OAB</span>
                <span className="inline-flex items-center gap-1 text-[9px] font-mono font-medium text-brand-400 bg-brand-500/10 px-1.5 py-0.5 rounded border border-brand-500/20">
                  <Lock className="w-2.5 h-2.5" />
                  Auto Odoo 18
                </span>
              </label>
              <input
                id="oab_folio"
                name="oab_folio"
                type="text"
                readOnly
                value={folio || 'Generando...'}
                title="Folio correlativo único gobernado por el servidor (ir.sequence)"
                className="w-full px-2.5 py-1 text-xs font-mono font-bold bg-surfaceSubtle border border-borderSubtle rounded text-brand-400 cursor-not-allowed select-all opacity-95"
              />
            </div>
            <div>
              <label htmlFor="oab_fecha_emision" className="block text-[10px] uppercase text-slate-500 font-semibold mb-1">
                Fecha de Emisión
              </label>
              <input
                id="oab_fecha_emision"
                name="oab_fecha_emision"
                type="date"
                readOnly={modalStage === 'EMITTED_SUCCESS'}
                value={fechaEmision}
                onChange={(e) => setFechaEmision(e.target.value)}
                className={`w-full px-2.5 py-1 text-xs font-mono bg-surface border border-borderSubtle rounded text-slate-200 ${modalStage === 'EMITTED_SUCCESS' ? 'cursor-not-allowed bg-surfaceSubtle opacity-90' : ''}`}
              />
            </div>
            <div>
              <label htmlFor="oab_tasa_bcv" className="flex items-center justify-between text-[10px] uppercase text-slate-500 font-semibold mb-1">
                <span>Tasa Oficial BCV (Bs/USD)</span>
                <span className="inline-flex items-center gap-1 text-[9px] font-mono font-medium text-amber-400 bg-amber-500/10 px-1.5 py-0.5 rounded border border-amber-500/20">
                  <svg className="w-2.5 h-2.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                  Verificada
                </span>
              </label>
              <input
                id="oab_tasa_bcv"
                name="oab_tasa_bcv"
                type="number"
                step="0.01"
                value={tasa}
                readOnly
                title="Tasa oficial sincronizada con BCV (bloqueada para garantizar paridad)"
                className="w-full px-2.5 py-1 text-xs font-mono bg-surfaceSubtle border border-borderSubtle rounded text-amber-300 font-bold cursor-not-allowed opacity-90 select-none"
              />
            </div>
            <div>
              <label htmlFor="oab_notas" className="block text-[10px] uppercase text-slate-500 font-semibold mb-1">
                Notas / Propósito
              </label>
              <input
                id="oab_notas"
                name="oab_notas"
                type="text"
                readOnly={modalStage === 'EMITTED_SUCCESS'}
                placeholder="Ej: Reposición urgente obras..."
                value={notas}
                onChange={(e) => setNotas(e.target.value)}
                className={`w-full px-2.5 py-1 text-xs bg-surface border border-borderSubtle rounded text-slate-200 placeholder-slate-500 ${modalStage === 'EMITTED_SUCCESS' ? 'cursor-not-allowed bg-surfaceSubtle opacity-90' : ''}`}
              />
            </div>
          </div>

          {/* Banner de Circuito Asistido de Re-solicitud (PLAN-039 / Thin Slice 5) */}
          {notas && notas.toLowerCase().includes('re-solicitud') && modalStage !== 'EMITTED_SUCCESS' && (
            <div className="bg-amber-950/40 border-b border-amber-500/30 px-4 py-2 flex flex-wrap items-center justify-between gap-2 text-xs text-amber-300">
              <div className="flex items-center gap-2">
                <RotateCcw className="w-4 h-4 text-amber-400 shrink-0" />
                <span>
                  <strong>Circuito de Re-solicitud Asistida:</strong> {notas}
                </span>
              </div>
              <span className="text-[10px] uppercase font-mono px-2 py-0.5 rounded bg-amber-500/20 text-amber-300 border border-amber-500/30 font-semibold">
                Precargado en Memoria (Borrador Local)
              </span>
            </div>
          )}

          {/* Modal Body: Modo Entrega Operativa Hand-off vs Modo Tabla de Selección */}
          {modalStage === 'EMITTED_SUCCESS' ? (
            <div className="flex-1 overflow-y-auto custom-scrollbar p-4 sm:p-6 space-y-6">
              {/* Banner de Confirmación Oficial */}
              <div className="bg-gradient-to-r from-emerald-950/40 via-surfaceHigh to-emerald-950/20 border border-emerald-500/40 rounded-2xl p-5 shadow-xl flex flex-col sm:flex-row items-center gap-5">
                <div className="w-14 h-14 rounded-2xl bg-emerald-500/20 text-emerald-300 border border-emerald-500/40 flex items-center justify-center shrink-0 shadow-lg shadow-emerald-500/10">
                  <CheckCircle className="w-8 h-8" />
                </div>
                <div className="flex-1 text-center sm:text-left">
                  <div className="flex flex-wrap items-center justify-center sm:justify-start gap-2 mb-1">
                    <span className="px-2 py-0.5 rounded-full text-[10px] font-bold tracking-wide uppercase bg-emerald-500/20 text-emerald-300 border border-emerald-500/40">
                      ✓ Asentado en Notion ERP
                    </span>
                    <span className="font-mono text-xs text-brand-400 font-bold bg-page px-2 py-0.5 rounded border border-borderSubtle">
                      Folio: {folio}
                    </span>
                  </div>
                  <h3 className="text-base sm:text-lg font-bold text-white tracking-tight">
                    ¡Orden de Abastecimiento Emitida con Éxito!
                  </h3>
                  <p className="text-xs text-slate-300 mt-1 leading-relaxed">
                    La requisición fue promovida al estado <strong className="text-emerald-300">Solicitado</strong> en Notion ERP con <strong>{lines.length} material(es)</strong> y un total de <strong className="text-amber-300">${totalUSD.toFixed(2)} USD (Bs {totalBs.toFixed(2)})</strong>. El borrador local fue purgado de forma segura.
                  </p>
                </div>
              </div>

              {/* Hub de Acciones de Entrega Operativa */}
              <div>
                <h4 className="text-xs uppercase tracking-wider text-slate-400 font-bold mb-3 flex items-center gap-1.5">
                  <Sparkles className="w-3.5 h-3.5 text-brand-400" />
                  <span>Acciones de Entrega Operativa (Hand-off)</span>
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                  {/* Atajo 1: Imprimir Hoja Viajera Carta */}
                  <button
                    type="button"
                    onClick={() => {
                      const headerObj: OABHeader = {
                        id: submittedHeader?.id || currentOabId || undefined,
                        folio,
                        fechaEmision,
                        totalUSD,
                        totalBs,
                        tasaBCV: tasa,
                        estadoGeneral: 'Solicitado',
                        notas
                      };
                      setSubmittedHeader(headerObj);
                      setHasPrintedRouteSheet(true);
                      setShowPrintSheet(true);
                    }}
                    className="group text-left p-4 rounded-xl bg-surfaceHigh hover:bg-surfaceHighest border border-borderSubtle hover:border-brand-500/40 transition shadow-md flex items-start gap-3.5 cursor-pointer"
                  >
                    <div className="p-2.5 rounded-xl bg-brand-500/10 text-brand-400 border border-brand-500/30 group-hover:bg-brand-500 group-hover:text-slate-950 transition shrink-0">
                      <Printer className="w-5 h-5" />
                    </div>
                    <div className="flex-1">
                      <div className="text-xs font-bold text-white group-hover:text-brand-300 transition flex items-center gap-1.5">
                        <span>Imprimir Hoja Viajera (Carta)</span>
                      </div>
                      <p className="text-[11px] text-slate-400 mt-1 leading-snug">
                        Documento físico oficial con firmas para entrega inmediata a Magaly y Compras.
                      </p>
                    </div>
                  </button>

                  {/* Atajo 2: Imprimir Rótulos de Bultos QR */}
                  <button
                    type="button"
                    onClick={() => setShowLabelsSheet(true)}
                    className="group text-left p-4 rounded-xl bg-surfaceHigh hover:bg-surfaceHighest border border-borderSubtle hover:border-purple-500/40 transition shadow-md flex items-start gap-3.5 cursor-pointer"
                  >
                    <div className="p-2.5 rounded-xl bg-purple-500/10 text-purple-400 border border-purple-500/30 group-hover:bg-purple-500 group-hover:text-slate-950 transition shrink-0">
                      <Tag className="w-5 h-5" />
                    </div>
                    <div className="flex-1">
                      <div className="text-xs font-bold text-white group-hover:text-purple-300 transition flex items-center gap-1.5">
                        <span>Imprimir Rótulos de Bultos (QR)</span>
                      </div>
                      <p className="text-[11px] text-slate-400 mt-1 leading-snug">
                        Etiquetas térmicas 4-UP / 8-UP con QR único por insumo para embalaje de taller.
                      </p>
                    </div>
                  </button>

                  {/* Atajo 3: Módulo de Revisión OAB */}
                  <button
                    type="button"
                    onClick={() => {
                      if (onOpenReview) {
                        onOpenReview(folio);
                      } else {
                        handleFinishAndClose();
                      }
                    }}
                    className="group text-left p-4 rounded-xl bg-surfaceHigh hover:bg-surfaceHighest border border-borderSubtle hover:border-sky-500/40 transition shadow-md flex items-start gap-3.5 cursor-pointer"
                  >
                    <div className="p-2.5 rounded-xl bg-sky-500/10 text-sky-400 border border-sky-500/30 group-hover:bg-sky-500 group-hover:text-slate-950 transition shrink-0">
                      <Eye className="w-5 h-5" />
                    </div>
                    <div className="flex-1">
                      <div className="text-xs font-bold text-white group-hover:text-sky-300 transition flex items-center gap-1.5">
                        <span>Ver en Módulo de Revisión OAB</span>
                      </div>
                      <p className="text-[11px] text-slate-400 mt-1 leading-snug">
                        Auditar cotizaciones, estado de aprobación gerencial y transcripción en vivo.
                      </p>
                    </div>
                  </button>

                  {/* Atajo 4: Iniciar Nueva Requisición */}
                  <button
                    type="button"
                    onClick={() => {
                      try {
                        sessionStorage.removeItem(DRAFT_STORAGE_KEY);
                      } catch (e) {}
                      initializeFreshDraft();
                    }}
                    className="group text-left p-4 rounded-xl bg-surfaceHigh hover:bg-surfaceHighest border border-borderSubtle hover:border-emerald-500/40 transition shadow-md flex items-start gap-3.5 cursor-pointer"
                  >
                    <div className="p-2.5 rounded-xl bg-emerald-500/10 text-emerald-400 border border-emerald-500/30 group-hover:bg-emerald-500 group-hover:text-slate-950 transition shrink-0">
                      <RotateCcw className="w-5 h-5" />
                    </div>
                    <div className="flex-1">
                      <div className="text-xs font-bold text-white group-hover:text-emerald-300 transition flex items-center gap-1.5">
                        <span>Iniciar Nueva Requisición</span>
                      </div>
                      <p className="text-[11px] text-slate-400 mt-1 leading-snug">
                        Comenzar una nueva solicitud de insumos con folio correlativo nuevo y limpio.
                      </p>
                    </div>
                  </button>

                  {/* Atajo 5: Panel Semi-Automático Multicanal (Telegram Tópico 145 y WhatsApp Almacén - PLAN-045) */}
                  <div className="sm:col-span-2">
                    <OABStageNotifyPanel
                      stage="EMITTED"
                      folio={folio}
                      oabId={submittedHeader?.id || currentOabId || undefined}
                      urgencySummary={computeOABUrgencySummary(lines)}
                      storesSummary={computeOABStoresSummary(lines)}
                      linesCount={lines.length}
                      routeSheetPrinted={hasPrintedRouteSheet}
                      operatorName={currentUser?.name || 'Operador de Planta'}
                      operatorRole={currentUser?.puestos?.[0] || 'Almacén / Taller'}
                      notes={notas}
                    />
                  </div>
                </div>
              </div>

              {/* Resumen de Materiales Emitidos (Read-only) */}
              <div className="border border-borderSubtle rounded-xl overflow-hidden bg-surface">
                <div className="px-4 py-2.5 bg-surfaceHigh border-b border-borderSubtle flex items-center justify-between text-xs">
                  <span className="font-semibold text-slate-300">
                    Resumen de Materiales Solicitados ({lines.length})
                  </span>
                  <span className="font-mono text-emerald-400 font-bold">
                    Total: ${totalUSD.toFixed(2)} USD
                  </span>
                </div>
                <div className="max-h-60 overflow-y-auto divide-y divide-borderSubtle/50 text-xs custom-scrollbar">
                  {lines.map((line, idx) => (
                    <div key={idx} className="p-2.5 px-4 flex items-center justify-between hover:bg-surfaceHigh/40">
                      <div className="min-w-0 pr-2">
                        <p className="font-medium text-slate-200 truncate">{line.nombre}</p>
                        <p className="text-[10px] text-slate-400 font-mono">
                          {line.cantidadSolicitada} {line.unidadMedida || 'UND'} • Prioridad: {line.prioridad || 'Media'}
                          {line.proyectoNombre && ` • ${line.proyectoNombre}`}
                        </p>
                      </div>
                      <div className="text-right shrink-0 font-mono">
                        <span className="text-slate-200 font-semibold">${(line.subtotalUSD || 0).toFixed(2)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            /* Lines Table */
            <div className="flex-1 overflow-y-auto custom-scrollbar p-3 sm:p-4 space-y-2">
            <div className="flex flex-wrap items-center justify-between gap-2 pb-1">
              <div className="flex flex-wrap items-center gap-3">
                <span className="text-xs uppercase font-semibold text-slate-400 tracking-wider">
                  Ítems Seleccionados ({lines.length})
                </span>

                {/* Control de Ordenamiento Multi-Criterio */}
                <div className="flex items-center gap-1.5 text-[11px] bg-surface border border-borderSubtle rounded-md px-2 py-0.5">
                  <label htmlFor="oab_sort_criterion" className="text-slate-500 font-semibold text-[10px]">Ordenar por:</label>
                  <select
                    id="oab_sort_criterion"
                    name="oab_sort_criterion"
                    value={sortCriterion}
                    onChange={(e) => setSortCriterion(e.target.value as SortCriterion)}
                    className="bg-transparent text-slate-200 font-medium focus:outline-none cursor-pointer"
                  >
                    <option value="subtotal" className="bg-surface">Subtotal ($)</option>
                    <option value="costo" className="bg-surface">Costo Unitario ($)</option>
                    <option value="nombre" className="bg-surface">Nombre (A-Z)</option>
                  </select>
                  <button
                    type="button"
                    onClick={() => setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc')}
                    className="p-1 text-slate-400 hover:text-white rounded hover:bg-surfaceHigh transition"
                    title={sortDirection === 'asc' ? 'Ascendente (menor a mayor)' : 'Descendente (mayor a menor)'}
                  >
                    <ArrowUpDown className="w-3 h-3" />
                  </button>
                </div>
              </div>

              {/* Buscador Reactivo de Insumos (Flujo Rápido de Taller) */}
              <div className="relative w-80 sm:w-96">
                <div className="relative flex items-center gap-1.5">
                  <div className="relative flex-1 flex items-center">
                    <Search className="w-3.5 h-3.5 absolute left-2.5 text-slate-400 pointer-events-none" />
                    <input
                      id="oab_search_insumo"
                      name="oab_search_insumo"
                      ref={searchInputRef}
                      type="text"
                      placeholder="🔍 Buscar para agregar (Enter rápido)..."
                      aria-label="Buscar insumo para agregar a la orden"
                      value={searchTerm}
                      onChange={(e) => {
                        setSearchTerm(e.target.value);
                        setIsSearchDropdownOpen(true);
                      }}
                      onFocus={() => setIsSearchDropdownOpen(true)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && searchResults.length > 0) {
                          e.preventDefault();
                          handleAddManualItem(searchResults[0]);
                          setSearchTerm('');
                          setIsSearchDropdownOpen(false);
                          searchInputRef.current?.focus();
                        } else if (e.key === 'Escape') {
                          setIsSearchDropdownOpen(false);
                        }
                      }}
                      className="w-full pl-8 pr-7 py-1 text-xs bg-surface border border-borderSubtle rounded-md text-slate-200 placeholder-slate-400 focus:outline-none focus:border-brand-500 focus:ring-1 focus:ring-brand-500/30 transition shadow-inner"
                    />
                    {searchTerm && (
                      <button
                        type="button"
                        onClick={() => {
                          setSearchTerm('');
                          setIsSearchDropdownOpen(false);
                          searchInputRef.current?.focus();
                        }}
                        className="absolute right-2 text-slate-400 hover:text-white p-0.5 rounded"
                        title="Limpiar búsqueda"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={handleOpenQuickAdd}
                    className="px-2 py-1 bg-blue-600/20 hover:bg-blue-600/40 text-blue-300 border border-blue-500/30 rounded text-xs font-semibold whitespace-nowrap flex items-center gap-1 shadow-sm active:scale-95"
                    title="Registrar nuevo insumo al vuelo (Alta Dual)"
                  >
                    <Plus className="w-3.5 h-3.5" />
                    <span className="hidden sm:inline">+ Insumo al Vuelo</span>
                  </button>
                </div>

                {/* Dropdown flotante predictivo */}
                {isSearchDropdownOpen && searchResults.length > 0 && (
                  <div className="absolute right-0 top-full mt-1.5 w-80 sm:w-96 bg-surfaceHigh border border-borderSubtle rounded-lg shadow-2xl z-30 max-h-60 overflow-y-auto custom-scrollbar divide-y divide-borderSubtle/50">
                    <div className="px-3 py-1 bg-surface text-[10px] uppercase font-mono text-slate-400 flex items-center justify-between">
                      <span>Resultados ({searchResults.length})</span>
                      <span className="text-brand-400 font-bold">↵ Enter para añadir</span>
                    </div>
                    {searchResults.map((item, idx) => (
                      <button
                        key={item.id}
                        type="button"
                        onMouseDown={(e) => {
                          e.preventDefault();
                          handleAddManualItem(item);
                          setSearchTerm('');
                          setIsSearchDropdownOpen(false);
                          searchInputRef.current?.focus();
                        }}
                        onClick={() => {
                          handleAddManualItem(item);
                          setSearchTerm('');
                          setIsSearchDropdownOpen(false);
                          searchInputRef.current?.focus();
                        }}
                        className={`w-full text-left px-3 py-2 flex items-center justify-between hover:bg-brand-500/20 transition ${
                          idx === 0 ? 'bg-brand-500/10' : ''
                        }`}
                      >
                        <div className="pr-2">
                          <p className="text-xs font-semibold text-slate-200 leading-tight">
                            {item.nombre}
                          </p>
                          <div className="flex items-center gap-2 mt-0.5 text-[10px] text-slate-400">
                            {item.codigo && <span className="font-mono text-cyan-400">[{item.codigo}]</span>}
                            {item.marca && <span>{item.marca}</span>}
                            {item.unidad && (
                              <span className="text-amber-400 font-mono">📦 {item.unidad}</span>
                            )}
                          </div>
                        </div>
                        <div className="text-right whitespace-nowrap">
                          <span className={`font-mono text-xs ${item.stockBase === 0 ? 'text-red-400 font-bold' : 'text-slate-300'}`}>
                            Stock: {item.stockBase}
                          </span>
                          {item.deficit > 0 && (
                            <span className="block text-[10px] text-orange-400 font-mono font-bold">-{item.deficit}</span>
                          )}
                        </div>
                      </button>
                    ))}
                    <div className="p-1.5 bg-slate-900 border-t border-borderSubtle/50 text-center">
                      <button
                        type="button"
                        onMouseDown={(e) => {
                          e.preventDefault();
                          handleOpenQuickAdd();
                        }}
                        className="w-full py-1 text-xs text-blue-400 hover:text-blue-300 font-semibold flex items-center justify-center gap-1 hover:bg-slate-800 rounded"
                      >
                        <Plus className="w-3.5 h-3.5" />
                        <span>+ Registrar Nuevo Insumo al Vuelo</span>
                      </button>
                    </div>
                  </div>
                )}

                {/* Dropdown cuando no hay resultados */}
                {isSearchDropdownOpen && searchResults.length === 0 && searchTerm.trim() && (
                  <div className="absolute right-0 top-full mt-1.5 w-80 sm:w-96 bg-surfaceHigh border border-borderSubtle rounded-lg shadow-2xl z-30 p-3 text-center space-y-2">
                    <p className="text-xs text-slate-400">
                      No se encontró "<span className="text-slate-200 font-semibold">{searchTerm}</span>" en el catálogo.
                    </p>
                    <button
                      type="button"
                      onMouseDown={(e) => {
                        e.preventDefault();
                        handleOpenQuickAdd();
                      }}
                      className="w-full py-1.5 px-3 bg-blue-600 hover:bg-blue-500 text-white text-xs font-bold rounded-lg flex items-center justify-center gap-1.5 shadow"
                    >
                      <Plus className="w-3.5 h-3.5" />
                      <span>Registrar "{searchTerm}" al Vuelo</span>
                    </button>
                  </div>
                )}
              </div>
            </div>

            {lines.length === 0 ? (
              <div className="py-16 text-center text-slate-500 text-xs">
                No hay ítems con déficit ni seleccionados en el borrador.
              </div>
            ) : (
              <div className="border border-borderSubtle rounded-lg overflow-x-auto custom-scrollbar">
                <table className="w-full text-xs text-left min-w-[760px]">
                  <thead className="bg-surfaceHigh text-slate-400 uppercase text-[10px] border-b border-borderSubtle sticky top-0 z-10 shadow-sm">
                    <tr>
                      <th className="p-2 w-8 text-center">#</th>
                      <th className="p-2">Insumo</th>
                      <th className="p-2 w-16 text-right">Stock</th>
                      <th className="p-2 w-16 text-right">Déficit</th>
                      <th className="p-2 w-36 text-right">Cant. Compra / UoM</th>
                      <th className="p-2 w-24 text-right">P. Unit ($)</th>
                      <th className="p-2 w-20 text-right">Subtotal</th>
                      <th className="p-2 w-28">Prioridad</th>
                      <th className="p-2 min-w-[190px]">Obra / Pedido</th>
                      <th className="p-2 w-10 text-center"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-borderSubtle/50">
                    {groupedLines.map(group => {
                      const theme = PRIORITY_THEME[group.priority];
                      return (
                        <React.Fragment key={group.priority}>
                          {/* Fila Encabezado de Prioridad con Subtotal en Pantalla */}
                          <tr className={`${theme.headerBg} border-t-2 border-b ${theme.borderClass}`}>
                            <td colSpan={10} className="py-1.5 px-3">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                  <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-[11px] font-bold border ${theme.badgeClass}`}>
                                    <span>{theme.icon}</span>
                                    <span>{theme.label}</span>
                                  </span>
                                  <span className="text-[11px] text-slate-400 font-mono">
                                    ({group.items.length} {group.items.length === 1 ? 'insumo' : 'insumos'})
                                  </span>
                                </div>
                                <div className="flex items-center gap-2 font-mono text-[11px]">
                                  <span className="text-slate-400 uppercase text-[10px] font-semibold">Subtotal Prioridad:</span>
                                  <span className="font-bold text-emerald-400">${group.subtotal.toFixed(2)} USD</span>
                                </div>
                              </div>
                            </td>
                          </tr>

                          {/* Renglones de la Prioridad */}
                          {group.items.map(({ line, globalIndex }) => {
                            const lineKey = getLineKey(line);
                            return (
                              <tr key={lineKey} className="hover:bg-surfaceHigh/30 transition">
                                <td className="p-2 text-center text-slate-500 font-mono">{globalIndex}</td>
                                <td className="p-2">
                                  <div className="flex flex-wrap items-center gap-2">
                                    <span className="font-semibold text-slate-200 block">{line.nombre}</span>
                                    {(line.rutaLogistica === 'SERVICIO_SITIO' || line.rutaLogistica === 'DIRECTO_OBRA') && (
                                      <span className="text-[9px] font-mono px-1.5 py-0.5 rounded bg-violet-500/20 text-violet-300 border border-violet-500/40">
                                        {line.rutaLogistica === 'SERVICIO_SITIO' ? '⚡ Servicio en Sitio' : '🚚 Envío Directo a Obra'}
                                      </span>
                                    )}
                                    {notas && notas.toLowerCase().includes('re-solicitud') && (
                                      <span className="text-[9px] font-mono px-1.5 py-0.2 rounded bg-amber-500/20 text-amber-300 border border-amber-500/30">
                                        🔄 Re-solicitado
                                      </span>
                                    )}
                                  </div>
                                  {line.empaqueComercial && (
                                    <span className="text-[10px] text-amber-400 font-mono flex items-center gap-1 mt-0.5">
                                      <span>📦 {line.empaqueComercial}</span>
                                      {line.paquetesSugeridos && line.factorEmpaque && line.factorEmpaque > 1 && (
                                        <span className="text-slate-400">
                                          ({line.paquetesSugeridos} {line.paquetesSugeridos === 1 ? 'paquete' : 'paquetes'})
                                        </span>
                                      )}
                                    </span>
                                  )}
                                </td>
                                <td className="p-2 text-right font-mono text-slate-400">{line.cantidadStock}</td>
                                <td className="p-2 text-right font-mono text-orange-400 font-bold">
                                  {line.deficit > 0 ? `-${line.deficit}` : '0'}
                                </td>
                                <td className="p-2 text-right">
                                  <div className="flex flex-col items-end gap-1">
                                    {line.deficit <= 0 && (
                                      <span className="text-[8px] font-mono text-amber-400 bg-amber-500/10 px-1 py-0.5 rounded border border-amber-500/30 font-semibold whitespace-nowrap">
                                        ⚠️ Definir cant.
                                      </span>
                                    )}
                                    <div className="flex items-center gap-1">
                                      <input
                                        id={`oab_qty_${lineKey}`}
                                        name={`oab_qty_${lineKey}`}
                                        type="number"
                                        min="1"
                                        value={line.cantidadCompra ?? line.cantidadSolicitada}
                                        aria-label={`Cantidad solicitada para ${line.nombre}`}
                                        onKeyDown={(e) => {
                                          if (['e', 'E', '+', '-'].includes(e.key)) e.preventDefault();
                                        }}
                                        onChange={(e) => handleQuantityChange(lineKey, Math.max(1, Number(e.target.value) || 0))}
                                        className={`w-20 px-2 py-1 text-right font-mono font-bold bg-page border rounded text-brand-400 focus:border-brand-400 focus:outline-none ${
                                          line.deficit <= 0
                                            ? 'border-amber-500/60 ring-1 ring-amber-500/30'
                                            : 'border-borderSubtle'
                                        }`}
                                      />
                                      <select
                                        id={`oab_uom_${lineKey}`}
                                        name={`oab_uom_${lineKey}`}
                                        value={line.uomCompra || line.unidadMedida || 'UND'}
                                        aria-label={`Unidad de compra para ${line.nombre}`}
                                        onChange={(e) => handleUomChange(lineKey, e.target.value)}
                                        className="bg-surface border border-borderSubtle rounded px-1.5 py-1 text-[11px] font-mono text-slate-300 focus:outline-none focus:border-brand-500 cursor-pointer"
                                      >
                                        {getCompatibleUoMs(line.unidadMedida || 'UND', { cant_en_bulto: line.cantEnBulto }).map(u => (
                                          <option key={u.code} value={u.code} className="bg-surface">
                                            {u.code}
                                          </option>
                                        ))}
                                      </select>
                                    </div>

                                    {/* Indicador de conversión en vivo si la UoM de compra difiere de la UoM base */}
                                    {line.uomCompra && line.unidadMedida && line.uomCompra !== line.unidadMedida && (
                                      <span className="text-[10px] font-mono text-cyan-400 font-semibold bg-cyan-950/30 px-1 py-0.5 rounded border border-cyan-800/40" title={line.formulaUoM}>
                                        ⚡ = {line.cantidadSolicitada} {line.unidadMedida}
                                      </span>
                                    )}

                                    {line.cantidadComercialSugerida && line.cantidadSugerida && line.cantidadComercialSugerida !== line.cantidadSugerida && (
                                      <div className="flex items-center gap-1 text-[9px] font-mono">
                                        <button
                                          type="button"
                                          onClick={() => handleQuantityChange(lineKey, line.cantidadComercialSugerida!)}
                                          title="Redondear al empaque cerrado sugerido"
                                          className={`px-1 py-0.5 rounded transition ${
                                            (line.cantidadCompra ?? line.cantidadSolicitada) === line.cantidadComercialSugerida
                                              ? 'bg-amber-500/20 text-amber-300 font-bold border border-amber-500/40'
                                              : 'text-slate-400 hover:text-slate-200 bg-surfaceHigh'
                                          }`}
                                        >
                                          Empaque ({line.cantidadComercialSugerida})
                                        </button>
                                        <button
                                          type="button"
                                          onClick={() => handleQuantityChange(lineKey, line.cantidadSugerida)}
                                          title="Cantidad neta calculada exacta"
                                          className={`px-1 py-0.5 rounded transition ${
                                            (line.cantidadCompra ?? line.cantidadSolicitada) === line.cantidadSugerida
                                              ? 'bg-blue-500/20 text-blue-300 font-bold border border-blue-500/40'
                                              : 'text-slate-400 hover:text-slate-200 bg-surfaceHigh'
                                          }`}
                                        >
                                          Neto ({line.cantidadSugerida})
                                        </button>
                                      </div>
                                    )}
                                  </div>
                                </td>
                                <td className="p-2 text-right">
                                  <div className="flex flex-col items-end">
                                    <input
                                      id={`oab_cost_${lineKey}`}
                                      name={`oab_cost_${lineKey}`}
                                      type="number"
                                      step="0.01"
                                      min="0"
                                      value={line.costoCompraUSD ?? line.costoUnitarioUSD}
                                      aria-label={`Costo unitario en USD para ${line.nombre}`}
                                      onKeyDown={(e) => {
                                        if (['e', 'E', '+', '-'].includes(e.key)) e.preventDefault();
                                      }}
                                      onChange={(e) => handleCostChange(lineKey, Math.max(0, Number(e.target.value) || 0))}
                                      className="w-16 px-1.5 py-1 text-right font-mono bg-page border border-borderSubtle rounded text-slate-200"
                                    />
                                    {line.uomCompra && line.unidadMedida && line.uomCompra !== line.unidadMedida && (
                                      <span className="text-[9px] font-mono text-slate-400 mt-0.5">
                                        ${(line.costoUnitarioUSD || 0).toFixed(2)}/{line.unidadMedida}
                                      </span>
                                    )}
                                  </div>
                                </td>
                                <td className="p-2 text-right font-mono font-bold text-slate-100">
                                  ${line.subtotalUSD.toFixed(2)}
                                </td>
                                <td className="p-2">
                                  <select
                                    id={`oab_prio_${lineKey}`}
                                    name={`oab_prio_${lineKey}`}
                                    value={line.prioridad}
                                    aria-label={`Prioridad para ${line.nombre}`}
                                    onChange={(e) => handlePriorityChange(lineKey, e.target.value)}
                                    className="text-[11px] bg-page border border-borderSubtle rounded px-1.5 py-1 text-slate-300"
                                  >
                                    <option value="Urgente">🔴 Urgente</option>
                                    <option value="Alta">🟠 Alta</option>
                                    <option value="Media">🟡 Media</option>
                                    <option value="Baja">🟢 Baja</option>
                                    <option value="Por Pedido">🔵 Por Pedido</option>
                                  </select>
                                </td>
                                <td className="p-2 min-w-[190px] whitespace-nowrap">
                                  {line.proyectoNombre ? (
                                    <div className="flex items-center gap-1 text-[11px] text-brand-400 font-mono truncate max-w-[180px]">
                                      <span className="truncate" title={line.proyectoNombre}>{line.proyectoNombre}</span>
                                      <button
                                        type="button"
                                        onClick={() => {
                                          setActiveLineIdForOrder(lineKey);
                                          setOrderModalOpen(true);
                                        }}
                                        className="text-slate-400 hover:text-white p-0.5 rounded hover:bg-surfaceHigh transition shrink-0"
                                        title="Cambiar obra o pedido"
                                      >
                                        ✎
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() => {
                                          setLines(prev => prev.map(l => getLineKey(l) === lineKey ? { ...l, pedidoId: undefined, proyectoId: undefined, proyectoNombre: undefined } : l));
                                        }}
                                        className="text-slate-500 hover:text-rose-400 p-0.5 rounded hover:bg-rose-500/10 transition shrink-0 text-xs font-bold"
                                        title="Desvincular obra (pasar a Stock Común MTS)"
                                      >
                                        ✕
                                      </button>
                                    </div>
                                  ) : (
                                    <div className="flex items-center gap-2">
                                      <span className="inline-flex items-center gap-1 text-[10px] text-amber-300 font-mono bg-amber-500/10 border border-amber-500/30 px-1.5 py-0.5 rounded shrink-0">
                                        <span>📦 Stock Común</span>
                                      </span>
                                      <button
                                        type="button"
                                        onClick={() => {
                                          setActiveLineIdForOrder(lineKey);
                                          setOrderModalOpen(true);
                                        }}
                                        className="text-[10px] text-slate-400 hover:text-brand-400 flex items-center gap-0.5 underline shrink-0 transition"
                                        title="Asignar a una obra o pedido específico"
                                      >
                                        <Link2 className="w-2.5 h-2.5" />
                                        <span>Vincular</span>
                                      </button>
                                    </div>
                                  )}
                                </td>
                                <td className="p-2 w-10 text-center shrink-0">
                                  <button
                                    onClick={() => handleRemoveLine(lineKey)}
                                    className="text-red-400 hover:text-red-300 transition p-1 hover:bg-red-500/10 rounded"
                                    title="Eliminar línea"
                                  >
                                    <Trash2 className="w-3.5 h-3.5" />
                                  </button>
                                </td>
                              </tr>
                            );
                          })}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          )}

          {/* Modal Footer with Totals & Actions */}
          {modalStage === 'EMITTED_SUCCESS' ? (
            <div className="p-3 sm:p-4 bg-surfaceHigh border-t border-borderSubtle flex flex-wrap items-center justify-between gap-3 sm:gap-4">
              <div className="flex items-center gap-2 text-xs text-emerald-400">
                <ShieldCheck className="w-4 h-4 shrink-0" />
                <span>Folio <strong className="font-mono text-white">{folio}</strong> resguardado exitosamente en Notion ERP. No hay cambios pendientes.</span>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={handleFinishAndClose}
                  className="px-4 py-2 text-xs font-semibold rounded-lg bg-surfaceHighest hover:bg-slate-700 text-slate-200 border border-borderSubtle transition active:scale-95 min-h-[36px] cursor-pointer"
                >
                  Finalizar y Salir
                </button>
                <button
                  type="button"
                  onClick={() => setShowLabelsSheet(true)}
                  className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold rounded-lg bg-surfaceHighest hover:bg-slate-700 text-purple-300 border border-purple-500/30 transition min-h-[36px] active:scale-95 cursor-pointer"
                >
                  <Tag className="w-4 h-4 text-purple-400" />
                  <span>Rótulos QR</span>
                </button>
                <button
                  type="button"
                  onClick={() => setShowPrintSheet(true)}
                  className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold rounded-lg bg-surfaceHighest hover:bg-slate-700 text-slate-200 border border-borderSubtle transition min-h-[36px] active:scale-95 cursor-pointer"
                >
                  <Printer className="w-4 h-4 text-brand-400" />
                  <span>Imprimir Carta</span>
                </button>
                <button
                  type="button"
                  onClick={() => {
                    if (onOpenReview) {
                      onOpenReview(folio);
                    } else {
                      handleFinishAndClose();
                    }
                  }}
                  className="flex items-center gap-1.5 px-4 py-2 text-xs font-semibold rounded-lg bg-sky-600 hover:bg-sky-500 text-white transition active:scale-95 min-h-[36px] shadow-sm cursor-pointer"
                >
                  <Eye className="w-4 h-4" />
                  <span>Ver en Revisión OAB</span>
                </button>
              </div>
            </div>
          ) : (
            <div className="p-3 sm:p-4 bg-surfaceHigh border-t border-borderSubtle flex flex-wrap items-center justify-between gap-3 sm:gap-4">
              {/* Totals */}
              <div className="flex items-center gap-4 sm:gap-6">
                <div>
                  <span className="text-[10px] uppercase text-slate-400 block font-semibold">Total Estimado ($ USD)</span>
                  <span className="text-lg sm:text-xl font-bold font-mono text-emerald-400">${totalUSD.toFixed(2)}</span>
                </div>
                <div className="border-l border-borderSubtle pl-4 sm:pl-6">
                  <span className="text-[10px] uppercase text-slate-400 block font-semibold">Total Estimado (Bs BCV)</span>
                  <span className="text-lg sm:text-xl font-bold font-mono text-amber-300">Bs {totalBs.toFixed(2)}</span>
                </div>
                {statusMessage && (
                  <div className="hidden md:flex items-center gap-1.5 text-xs text-brand-400 bg-page px-3 py-1 rounded border border-brand-500/30">
                    <CheckCircle className="w-3.5 h-3.5" />
                    <span>{statusMessage}</span>
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={handleAttemptClose}
                  className="px-3 py-2 text-xs text-slate-400 hover:text-white min-h-[36px] cursor-pointer"
                >
                  Cerrar
                </button>

                <button
                  type="button"
                  onClick={handleSaveDraftCloud}
                  disabled={isSavingDraftCloud || isSubmitting || lines.length === 0}
                  className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold rounded-lg bg-surfaceHighest hover:bg-slate-700 text-purple-300 border border-purple-500/30 transition min-h-[36px] active:scale-95 disabled:opacity-50 cursor-pointer"
                  title="Guardar borrador en Notion ERP sin emitirlo todavía"
                >
                  {isSavingDraftCloud ? (
                    <div className="w-3.5 h-3.5 border-2 border-purple-400 border-t-transparent rounded-full animate-spin"></div>
                  ) : (
                    <CloudUpload className="w-4 h-4 text-purple-400" />
                  )}
                  <span>Guardar Borrador (Nube)</span>
                </button>

                <button
                  type="button"
                  onClick={() => {
                    const headerObj: OABHeader = {
                      folio,
                      fechaEmision,
                      totalUSD,
                      totalBs,
                      tasaBCV: tasa,
                      estadoGeneral: 'Solicitado',
                      notas
                    };
                    setSubmittedHeader(headerObj);
                    setHasPrintedRouteSheet(true);
                    setShowPrintSheet(true);
                  }}
                  className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold rounded-lg bg-surfaceHighest hover:bg-slate-700 text-slate-200 border border-borderSubtle transition min-h-[36px] active:scale-95 cursor-pointer"
                  title="Generar vista de impresión física para Magaly y Compras"
                >
                  <Printer className="w-4 h-4 text-brand-400" />
                  <span>Imprimir Hoja Viajera</span>
                </button>

                <button
                  type="button"
                  onClick={handleSaveToNotion}
                  disabled={isSubmitting || lines.length === 0}
                  className="flex items-center gap-1.5 px-4 py-2 text-xs font-semibold rounded-lg bg-brand-500 hover:bg-brand-600 text-slate-950 transition disabled:opacity-50 active:scale-95 min-h-[36px] shadow-sm shadow-brand-500/20 cursor-pointer"
                >
                  {isSubmitting ? (
                    <div className="w-3.5 h-3.5 border-2 border-slate-900 border-t-transparent rounded-full animate-spin"></div>
                  ) : (
                    <CheckCircle className="w-4 h-4" />
                  )}
                  <span>Emitir en Notion ERP</span>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modal de Búsqueda de Pedidos ERP */}
      {orderModalOpen && (
        <OrderSearchModal
          isOpen={orderModalOpen}
          onClose={() => {
            setOrderModalOpen(false);
            setActiveLineIdForOrder(null);
          }}
          onSelectOrder={handleSelectOrderForLine}
        />
      )}

      {/* Hoja Viajera Física de Impresión */}
      {showPrintSheet && submittedHeader && (
        <PrintSheetOAB
          header={submittedHeader}
          lineas={lines}
          onClose={() => setShowPrintSheet(false)}
        />
      )}

      {/* Rótulos de Bultos Físicos de Impresión QR (PLAN-033) */}
      {showLabelsSheet && (
        <PrintSheetProjectLabels
          items={lines.map(line => ({
            id: line.dashboardId || line.insumoId || line.nombre,
            insumoId: line.insumoId,
            dashboardId: line.dashboardId,
            nombre: line.nombre,
            codigo: line.dashboardId,
            categoria: '',
            folioOAB: folio,
            fechaRecepcion: fechaEmision || new Date().toISOString().split('T')[0],
            proyectoNombre: line.proyectoNombre || 'GENERAL / STOCK',
            cantidadRecibidaHoy: line.cantidadSolicitada || 1,
            bultos: Math.max(1, Math.ceil((line.cantidadSolicitada || 1) / (line.cantEnBulto || 1))),
            cantEnBulto: line.cantEnBulto || 1,
            unidadMedida: line.unidadMedida || 'UND'
          }))}
          onClose={() => setShowLabelsSheet(false)}
        />
      )}

      {/* Sub-modal 1: Alta Rápida de Insumo al Vuelo (PLAN-044) */}
      <QuickAddCatalogItemModal
        isOpen={isQuickAddOpen}
        onClose={() => setIsQuickAddOpen(false)}
        onItemCreated={(newItem) => {
          handleAddManualItem(newItem);
          if (onItemCreated) onItemCreated(newItem);
          setSearchTerm('');
          setIsSearchDropdownOpen(false);
        }}
      />

      {/* Sub-modal 2: Bienvenida / Restaurar Borrador (PLAN-044) */}
      <OABDraftRestoreModal
        isOpen={showDraftWelcomeModal}
        pendingDraft={pendingDraft}
        onRestore={handleRestoreDraft}
        onDiscardAndStartFresh={handleDiscardDraftAndStartFresh}
      />

      {/* Sub-modal 3: Diálogo Tripartito de Salida Segura (PLAN-044) */}
      <OABExitConfirmModal
        isOpen={showExitConfirmModal}
        itemsCount={lines.length}
        onSaveDraftAndExit={handleSaveDraftAndExit}
        onDiscardAndExit={handleDiscardAndExit}
        onContinueEditing={() => setShowExitConfirmModal(false)}
      />
    </>
  );
};
